/* eslint-disable no-console */
/* eslint-disable max-len */
import { Button, Icon, Paper } from '@material-ui/core';
import { capitalizeAll, ModeAutoCompleteInputField, ModeSelectInputField, ModeTextInputField, ReadonlyInputStyle, SelectInputOption,
    useModeFormStyle,
    usePropValue } from '@moderepo/mode-app-components';
import { DeviceClass, EntityClass } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import produce from 'immer';
import { useCallback, useMemo, useState } from 'react';
import { DEVICE_NAME_LENGTH_MAX, DEVICE_NAME_LENGTH_MIN, RegisterDeviceStepProps, useDeviceRegistrationWizardStyle } from '.';
import { ENTITY_DEVICE_ID_ATTRIBUTE, useSelectTranslatedEntityClasses } from '../..';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useGetAllProjectHomes, useSelectProject, useSelectProjectDeviceClasses, useSelectProjectFirmwareChannels }
    from '../../../utils/dataStateHooks';
import { DeviceRegistrationWizardSettings } from '../../../utils/ritzUIExtensionUtils';
import { createTimeZoneOptions } from '../../../utils/utils';


/**
 * The structure for the errors in this step
 */
interface StepInputErrors {
    deviceClass?: string | undefined;
    homeId?: string | undefined;
    channelName?: string | undefined;
    deviceName?: string | undefined;
    timeZone?: string | undefined;
}


/**
 * Given the array of device classes that are configured in the "deviceRegistrationWizard" settings, return the subset of the array by filtering
 * out all the invalid device class settings.
 * NOTE that the list of deviceClassesSettings are NOT always exactly the same as the list of project's device classes. It is possible
 * that a project has 5 device classes but only 3 device classes are configured in the "deviceRegistrationWizard" settings therefore, we
 * will only show 3 device classes for the user to choose, not 5. Or sometime a device class configured in the "deviceRegistrationWizard"
 * does not exist so we will not show it either.
 */
const filterDeviceClassesSettings = (
    deviceClassesSettings: DeviceRegistrationWizardSettings['deviceClassesSettings'],
    projectDeviceClasses: readonly DeviceClass[] | undefined,
    projectEntityClasses: readonly EntityClass[] | undefined,
): DeviceRegistrationWizardSettings['deviceClassesSettings'] => {

    // Wait until both projectDeviceClasses and projectEntityClasses are loaded before we can build the list of DeviceClass options.
    if (!projectDeviceClasses || !projectEntityClasses) {
        return [];
    }

    // Filter out all the invalid device classes defined in the "deviceRegistrationWizardSettings"
    // Because these are manually created by the admin, it is possible that the admin added device classes or entity classes that don't exist
    const filteredDeviceClasses = deviceClassesSettings.filter((deviceClassSettings) => {

        // Find an existing device class that matches the deviceClassSettings.deviceClass name
        const existingDeviceClass = projectDeviceClasses.find((projectDeviceClass) => {
            return projectDeviceClass.id === deviceClassSettings.deviceClass;
        });

        // If we can't find a device class in the project that matches the deviceClass configured in deviceRegistrationWizard then show
        // a warning so that the developers can see why some of the device classes don't show up in the options.
        if (!existingDeviceClass) {
            console.log(`DeviceClass '${deviceClassSettings.deviceClass}' not found`);
            return false;
        }

        // Find an existing entity class that matches the deviceClassSettings.entityClass name
        const existingEntityClass = projectEntityClasses.find((projectEntityClass) => {
            return projectEntityClass.entityClass === deviceClassSettings.entityClass;
        });

        if (!existingEntityClass) {
            console.log(`EntityClass '${deviceClassSettings.entityClass}' not found`);
            return false;
        }

        // Make sure the existingEntityClass has an attribute with key='_deviceId_'
        if (!existingEntityClass.attributeSchema?.some((schema) => {
            return schema.key === ENTITY_DEVICE_ID_ATTRIBUTE;
        })) {
            console.log(`EntityClass '${deviceClassSettings.entityClass}' does not have an attribute with key='${ENTITY_DEVICE_ID_ATTRIBUTE}'.`);
            return false;
        }

        return true;
    });

    return filteredDeviceClasses;
};



/**
 * The required props for the DeviceRegistrationCreateDeviceStep in addition to the props inherited from RegisterDeviceStep
 */
export interface DeviceRegistrationCreateDeviceStepProps extends RegisterDeviceStepProps {
    readonly deviceClass?: string | undefined;
    readonly homeId?: number | undefined;
    readonly channelName?: string | undefined;
    readonly deviceName?: string | undefined;
    readonly timeZone?: string | undefined;
    readonly onStepCompleted: (deviceClass: string, homeId: number, channelName: string, deviceName: string, timeZone: string)=> void;
}


/**
 * This is the Create Device step which is the step which the user needs to provide the device info e.g. deviceClass, homeId, tags, etc...
 */
export const DeviceRegistrationCreateDeviceStep: React.FC<DeviceRegistrationCreateDeviceStepProps> = (
    props: DeviceRegistrationCreateDeviceStepProps,
) => {
    const project = useSelectProject(props.projectId);
    const allChannels = useSelectProjectFirmwareChannels(props.projectId);
    const allHomes = useGetAllProjectHomes(props.projectId);
    const { trans } = useRitzTranslation(project);

    const projectDeviceClasses = useSelectProjectDeviceClasses(props.projectId);
    const projectEntityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);

    const sharedClasses = useDeviceRegistrationWizardStyle();
    const formClasses = useModeFormStyle();

    const [deviceClassInput, setDeviceClassInput] = usePropValue<string | undefined>(props.deviceClass);
    const [homeIdInput, setHomeIdInput] = usePropValue<number | undefined>(props.homeId);
    const [channelNameInput, setChannelNameInput] = usePropValue<string | undefined>(props.channelName);
    const [deviceNameInput, setDeviceNameInput] = usePropValue<string | undefined>(props.deviceName);
    const [timeZoneInput, setTimeZoneInput] = usePropValue<string | undefined>(props.timeZone);
    const [inputErrors, setInputErrors] = useState<StepInputErrors>({});


    /**
     * Get the device classes configured in deviceRegistrationWizardSettings and create and cache the list of device class options to be used for
     * the Select Device Class. Note that not all "deviceRegistrationWizardSettings"
     */
    const deviceClassOptions = useMemo((): readonly SelectInputOption<string>[] => {

        const filteredDeviceClasses = filterDeviceClassesSettings(
            props.deviceRegistrationWizardSettings.deviceClassesSettings, projectDeviceClasses, projectEntityClasses,
        );

        // Create an Option for each filteredDeviceClass
        return filteredDeviceClasses.map((deviceClassSettings) => {
            return {
                label: deviceClassSettings.deviceClass,
                value: deviceClassSettings.deviceClass,
            };
        });
    }, [projectDeviceClasses, projectEntityClasses, props.deviceRegistrationWizardSettings.deviceClassesSettings]);


    /**
     * Get the list of channels for the selected entity class
     */
    const filteredChannels = useMemo(() => {
        return allChannels?.filter((channel) => {
            return channel.deviceClass === deviceClassInput;
        });
    }, [allChannels, deviceClassInput]);


    /**
     * Create a cache the list of channel options use for the Select Channel input field.
     */
    const channelOptions = useMemo((): readonly SelectInputOption<string>[] => {
        return filteredChannels?.map((channel) => {
            // For each supported deviceClass, create an option for it.
            return {
                label: channel.name,
                value: channel.name,
            };
        }) ?? [];
    }, [filteredChannels]);



    /**
     * Create and cache the options to be used for the Select Home
     */
    const homeOptions = useMemo((): readonly SelectInputOption<number>[] => {
        return allHomes?.map((home) => {
            return {
                label   : `${home.name} #${home.id}`,
                value   : home.id,
                // If homeId is preselected then disable all the home options EXCEPT for the selected home
                disabled: props.homeId !== undefined && props.homeId !== home.id,
            };
        }) ?? [];
    }, [allHomes, props.homeId]);


    // Create the Dropdown Options for the select timeZone input
    const timeZoneOptions = useMemo((): readonly SelectInputOption<string>[] => {
        return createTimeZoneOptions(false, capitalizeAll(trans('label.none')));
    }, [trans]);



    /**
     * Submit the user inputs for this step. Before submitting the inputs, we will validate the inputs. If there are no errors,
     * we will notify the parent container by calling ths 'onStepCompleted' callback and provide the parent container with the new
     * inputs and return 'true' for success. If there are errors, we will notify the parent container that there are errors but only
     * if 'dispatchOnError' is true and also return false for failure.
     */
    const submitInputs = useCallback((dispatchOnError: boolean = true): boolean => {

        const newInputErrors: StepInputErrors = { };

        if (!deviceClassInput) {
            newInputErrors.deviceClass = 'error.input.generic.empty';
        }
        if (!homeIdInput) {
            newInputErrors.homeId = 'error.input.generic.empty';
        }
        if (!channelNameInput) {
            newInputErrors.channelName = 'error.input.generic.empty';
        }
        if (!deviceNameInput) {
            newInputErrors.deviceName = 'error.input.generic.empty';
        } else if (deviceNameInput.trim().length < DEVICE_NAME_LENGTH_MIN || deviceNameInput.trim().length > DEVICE_NAME_LENGTH_MAX) {
            newInputErrors.deviceName = 'error.input.generic.length';
        }

        if (!timeZoneInput) {
            newInputErrors.timeZone = 'error.input.generic.empty';
        }

        // Update the local inputErrors
        setInputErrors(newInputErrors);


        // If there is AT LEAST 1 field in the newInputError, this Step is considered to contain errors.
        const hasErrors = Object.values(newInputErrors).some((error) => {
            return error !== undefined;
        });


        // If 'dispatchOnError' is specified, dispatch the error or null to the container
        if (dispatchOnError) {
            props.onStepError(hasErrors);
        }


        // If there are no errors, notify the parent container this step is completed
        if (!hasErrors && deviceClassInput && homeIdInput && channelNameInput && deviceNameInput && timeZoneInput) {
            props.onStepCompleted(
                deviceClassInput, homeIdInput, channelNameInput, deviceNameInput.trim(), timeZoneInput,
            );
        }

        return !hasErrors;
    }, [channelNameInput, deviceClassInput, deviceNameInput, homeIdInput, props, timeZoneInput]);


    return (
        <div className={sharedClasses.stepContentBody}>
            <div className={clsx(sharedClasses.stepContentForm)}>
                <div className={clsx(sharedClasses.stepSubtitle)}>
                    {trans('device_registration_wizard.step_create_device.subtitle')}
                </div>
                <Paper elevation={1} className={clsx(sharedClasses.formControlGroup)}>

                    <ModeSelectInputField
                        className={clsx(formClasses.formInputField, sharedClasses.formControl, 'select-input')}
                        fieldName="deviceClass"
                        required
                        editable={!props.targetDevice}
                        readonlyStyle={ReadonlyInputStyle.FADE}
                        isInEditMode
                        label={capitalizeAll(trans('terms.device_class'))}
                        value={deviceClassInput ?? ''}
                        error={inputErrors.deviceClass ? trans(inputErrors.deviceClass) : undefined}
                        options={deviceClassOptions ?? []}
                        onInputChange={(fieldName, newValue) => {
                            // Because a different deviceClass is selected, we will need to clear the channel name input
                            setChannelNameInput(undefined);

                            // Input changed so clear the error and update the state
                            props.onStepError(false);
                            setInputErrors(produce(inputErrors, (draft) => {
                                delete draft.deviceClass;
                            }));
                            setDeviceClassInput(newValue);
                        }}
                    />

                    <ModeSelectInputField
                        className={clsx(formClasses.formInputField, sharedClasses.formControl, 'select-input')}
                        fieldName="homeId"
                        required
                        editable={!props.targetDevice?.homeId}
                        readonlyStyle={ReadonlyInputStyle.FADE}
                        isInEditMode
                        label={capitalizeAll(trans('terms.home'))}
                        value={homeIdInput ?? ''}
                        error={inputErrors.homeId ? trans(inputErrors.homeId) : undefined}
                        options={homeOptions ?? []}
                        onInputChange={(fieldName, newValue) => {
                            // Input changed so clear the error and update the state
                            props.onStepError(false);
                            setInputErrors(produce(inputErrors, (draft) => {
                                delete draft.homeId;
                            }));
                            setHomeIdInput(newValue);
                        }}
                    />

                    <ModeSelectInputField
                        className={clsx(formClasses.formInputField, sharedClasses.formControl, 'select-input')}
                        fieldName="channelName"
                        required
                        editable={true}
                        readonlyStyle={ReadonlyInputStyle.FADE}
                        isInEditMode
                        label={capitalizeAll(trans('label.firmware_channel'))}
                        value={channelNameInput ?? ''}
                        error={inputErrors.channelName ? trans(inputErrors.channelName) : undefined}
                        options={channelOptions ?? []}
                        onInputChange={(fieldName, newValue) => {
                            // Input changed so clear the error and update the state
                            props.onStepError(false);
                            setInputErrors(produce(inputErrors, (draft) => {
                                delete draft.channelName;
                            }));
                            setChannelNameInput(newValue);
                        }}
                    />

                    <ModeTextInputField
                        fieldName="deviceName"
                        className={clsx(formClasses.formInputField, sharedClasses.formControl, 'deviceName')}
                        required
                        editable={true}
                        readonlyStyle={ReadonlyInputStyle.FADE}
                        isInEditMode
                        label={capitalizeAll(trans('label.name'))}
                        error={inputErrors.deviceName ? trans(inputErrors.deviceName, {
                            min: DEVICE_NAME_LENGTH_MIN, max: DEVICE_NAME_LENGTH_MAX,
                        }) : undefined}
                        value={deviceNameInput || ''}
                        onInputChange={(fieldName, value) => {
                            // Input changed so clear the error and update the state
                            props.onStepError(false);
                            setInputErrors(produce(inputErrors, (draft: StepInputErrors) => {
                                delete draft.deviceName;
                            }));
                            setDeviceNameInput(value);
                        }}
                    />

                    <ModeAutoCompleteInputField
                        className={clsx(formClasses.formInputField, sharedClasses.formControl, 'select-input')}
                        fieldName="timeZone"
                        required
                        // `timeZone` is not editable anymore if we already created an Entity
                        editable={!props.targetEntity}
                        readonlyStyle={ReadonlyInputStyle.FADE}
                        isInEditMode
                        multiple={false}
                        label={capitalizeAll(trans('label.time_zone'))}
                        value={timeZoneInput ?? ''}
                        error={inputErrors.timeZone ? trans(inputErrors.timeZone) : undefined}
                        options={timeZoneOptions ?? []}
                        onInputChange={(fieldName, newValue) => {
                            // Input changed so clear the error and update the state
                            props.onStepError(false);
                            setInputErrors(produce(inputErrors, (draft) => {
                                delete draft.timeZone;
                            }));
                            setTimeZoneInput(newValue);
                        }}
                    />

                </Paper>
            </div>

            <div className={sharedClasses.stepContentActionsContainer}>
                <div className={sharedClasses.stepContentRightActionsContainer}>
                    <Button
                        disabled={!props.canGoBack}
                        onClick={() => {
                            // Validate the inputs AND dispatch onComplete if successful so that we can save the user inputs before going back.
                            // However, if inputs are invalid, we will not dispatch error and just take the user back to the previous step. We will
                            // not save the user's input since it is invalid anyway.
                            props.onStepError(false);
                            submitInputs(false);
                            props.onPrevStepClick();
                        }}
                        className={sharedClasses.navButton}
                        startIcon={<Icon>navigate_before</Icon>}
                    >
                        {capitalizeAll(trans('label.back'))}
                    </Button>
                    <Button
                        onClick={() => {
                            if (submitInputs()) {
                                props.onNextStepClick();
                            }
                        }}
                        className={sharedClasses.navButton}
                        endIcon={<Icon>navigate_next</Icon>}
                    >
                        {capitalizeAll(trans(props.isLastStep ? 'label.finish' : 'label.next'))}
                    </Button>
                </div>
            </div>
        </div>
    );
};
